import { useState, useEffect, useRef, useCallback } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
    'pdfjs-dist/build/pdf.worker.min.mjs',
    import.meta.url,
).href;
import {
    Box, Paper, Typography, Button, IconButton, Dialog, DialogTitle,
    DialogContent, DialogActions, TextField, Switch, FormControlLabel,
    Tooltip, Chip, CircularProgress, Stack, Divider, List, ListItem,
    ListItemText, ListItemSecondaryAction, Alert,
} from '@mui/material';
import {
    Upload as UploadIcon, Delete as DeleteIcon, Edit as EditIcon,
    Visibility as ViewIcon, Download as DownloadIcon, Add as AddIcon,
    Close as CloseIcon, ArrowUpward as UpIcon, ArrowDownward as DownIcon,
    ChevronLeft as PrevPageIcon, ChevronRight as NextPageIcon,
} from '@mui/icons-material';
import { agreementsAPI } from '../services/apiService';
import { useAlert } from '../contexts/AlertContext';

export default function AgreementFormsManager() {
    const { showToast, showAlert } = useAlert();
    const [forms, setForms] = useState([]);
    const [loading, setLoading] = useState(true);

    // Upload dialog
    const [uploadOpen, setUploadOpen] = useState(false);
    const [uploadFile, setUploadFile] = useState(null);
    const [uploadTitle, setUploadTitle] = useState('');
    const [uploadRequire, setUploadRequire] = useState(true);
    const [uploading, setUploading] = useState(false);
    const uploadInputRef = useRef(null);

    // Edit metadata dialog
    const [editForm, setEditForm] = useState(null);
    const [editTitle, setEditTitle] = useState('');
    const [editActive, setEditActive] = useState(true);
    const [editRequire, setEditRequire] = useState(true);
    const [editSortOrder, setEditSortOrder] = useState(0);
    const [saving, setSaving] = useState(false);

    // Placement editor dialog
    const [placementForm, setPlacementForm] = useState(null);
    const [placements, setPlacements] = useState([]);
    const [pdfDoc, setPdfDoc] = useState(null);
    const [pdfPages, setPdfPages] = useState(1);
    const [currentPage, setCurrentPage] = useState(0);
    const [activeTool, setActiveTool] = useState(null); // 'Signature'|'Date'|'PrintedName'
    const canvasRef = useRef(null);
    const placementSavingRef = useRef(false);

    // PDF viewer
    const [viewerUrl, setPdfViewerUrl] = useState(null);
    const [viewerOpen, setViewerOpen] = useState(false);
    const [viewerLoading, setViewerLoading] = useState(false);

    const fetchForms = useCallback(async () => {
        setLoading(true);
        try {
            const data = await agreementsAPI.getAll();
            setForms(data);
        } catch {
            showToast('Failed to load agreement forms', 'error');
        } finally {
            setLoading(false);
        }
    }, [showToast]);

    useEffect(() => { fetchForms(); }, [fetchForms]);

    // ── Upload ──────────────────────────────────────────────────────────────

    const handleUploadSubmit = async () => {
        if (!uploadFile) { showToast('Please select a PDF file', 'warning'); return; }
        if (!uploadTitle.trim()) { showToast('Title is required', 'warning'); return; }
        setUploading(true);
        try {
            await agreementsAPI.upload(uploadTitle.trim(), uploadFile, uploadRequire, forms.length);
            showToast('Agreement form uploaded', 'success');
            setUploadOpen(false);
            setUploadFile(null);
            setUploadTitle('');
            setUploadRequire(true);
            fetchForms();
        } catch {
            showToast('Upload failed', 'error');
        } finally {
            setUploading(false);
        }
    };

    // ── Edit meta ───────────────────────────────────────────────────────────

    const openEdit = (form) => {
        setEditForm(form);
        setEditTitle(form.title);
        setEditActive(form.isActive);
        setEditRequire(form.requireSignature);
        setEditSortOrder(form.sortOrder);
    };

    const handleEditSave = async () => {
        setSaving(true);
        try {
            await agreementsAPI.updateMeta(editForm.id, {
                title: editTitle.trim(),
                isActive: editActive,
                requireSignature: editRequire,
                sortOrder: editSortOrder,
                fieldPlacementsJson: editForm.fieldPlacementsJson ?? null,
            });
            showToast('Saved', 'success');
            setEditForm(null);
            fetchForms();
        } catch {
            showToast('Save failed', 'error');
        } finally {
            setSaving(false);
        }
    };

    // ── Delete ──────────────────────────────────────────────────────────────

    const handleDelete = (form) => {
        showAlert(
            'Delete Agreement Form',
            `Delete "${form.title}"? This cannot be undone. Previously signed copies are not affected.`,
            [
                { label: 'Cancel', variant: 'outlined' },
                {
                    label: 'Delete', color: 'error', variant: 'contained',
                    onClick: async () => {
                        try {
                            await agreementsAPI.deleteForm(form.id);
                            showToast('Deleted', 'success');
                            fetchForms();
                        } catch {
                            showToast('Delete failed', 'error');
                        }
                    },
                },
            ]
        );
    };

    // ── Reorder ─────────────────────────────────────────────────────────────

    const handleReorder = async (form, direction) => {
        const idx = forms.findIndex(f => f.id === form.id);
        const targetIdx = direction === 'up' ? idx - 1 : idx + 1;
        if (targetIdx < 0 || targetIdx >= forms.length) return;

        const other = forms[targetIdx];
        try {
            await Promise.all([
                agreementsAPI.updateMeta(form.id, { ...form, sortOrder: other.sortOrder, fieldPlacementsJson: form.fieldPlacementsJson ?? null }),
                agreementsAPI.updateMeta(other.id, { ...other, sortOrder: form.sortOrder, fieldPlacementsJson: other.fieldPlacementsJson ?? null }),
            ]);
            fetchForms();
        } catch {
            showToast('Reorder failed', 'error');
        }
    };

    // ── PDF viewer ──────────────────────────────────────────────────────────

    const handleView = async (form) => {
        setViewerLoading(true);
        setViewerOpen(true);
        try {
            const blob = await agreementsAPI.downloadBlank(form.id);
            const url = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
            setPdfViewerUrl(url);
        } catch {
            showToast('Failed to load PDF', 'error');
            setViewerOpen(false);
        } finally {
            setViewerLoading(false);
        }
    };

    const closeViewer = () => {
        setViewerOpen(false);
        if (viewerUrl) { window.URL.revokeObjectURL(viewerUrl); setPdfViewerUrl(null); }
    };

    const handleDownload = async (form) => {
        try {
            const blob = await agreementsAPI.downloadBlank(form.id);
            const url = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${form.title}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch {
            showToast('Download failed', 'error');
        }
    };

    // ── Placement editor ────────────────────────────────────────────────────

    // ── Render PDF page to canvas whenever page/doc changes ───────────────────
    useEffect(() => {
        if (!pdfDoc || !placementForm) return;
        let cancelled = false;
        (async () => {
            try {
                const page = await pdfDoc.getPage(currentPage + 1);
                if (cancelled || !canvasRef.current) return;
                const canvas = canvasRef.current;
                const viewport = page.getViewport({ scale: 1.5 });
                canvas.width = Math.round(viewport.width);
                canvas.height = Math.round(viewport.height);
                await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
            } catch (err) {
                if (!cancelled) console.error('PDF render error:', err);
            }
        })();
        return () => { cancelled = true; };
    }, [pdfDoc, currentPage, placementForm]);

    const openPlacementEditor = async (form) => {
        setPlacementForm(form);
        try {
            const parsed = form.fieldPlacementsJson ? JSON.parse(form.fieldPlacementsJson) : [];
            setPlacements(parsed);
        } catch {
            setPlacements([]);
        }
        setCurrentPage(0);
        setActiveTool(null);
        setPdfDoc(null);

        try {
            const blob = await agreementsAPI.downloadBlank(form.id);
            const arrayBuffer = await blob.arrayBuffer();
            const loadingTask = pdfjsLib.getDocument({ data: new Uint8Array(arrayBuffer) });
            const doc = await loadingTask.promise;
            setPdfPages(doc.numPages);
            setPdfDoc(doc);
        } catch {
            showToast('Could not load PDF for placement', 'error');
            setPlacementForm(null);
        }
    };

    const closePlacementEditor = () => {
        setPlacementForm(null);
        setPlacements([]);
        if (pdfDoc) {
            try { pdfDoc.destroy(); } catch { }
            setPdfDoc(null);
        }
    };

    const handlePlacementClick = (e) => {
        if (!activeTool || !canvasRef.current) return;
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        if (e.clientX < rect.left || e.clientX > rect.right ||
            e.clientY < rect.top || e.clientY > rect.bottom) return;
        const xNorm = (e.clientX - rect.left) / rect.width;
        const yNorm = (e.clientY - rect.top) / rect.height;
        const defaultW = activeTool === 'Signature' ? 0.30 : 0.20;
        const defaultH = activeTool === 'Signature' ? 0.05 : 0.03;

        setPlacements(prev => {
            // Replace existing placement of same type on same page
            const filtered = prev.filter(p => !(p.fieldType === activeTool && p.page === currentPage));
            return [...filtered, {
                fieldType: activeTool,
                page: currentPage,
                xNorm: Math.max(0, Math.min(xNorm, 1 - defaultW)),
                yNorm: Math.max(0, Math.min(yNorm, 1 - defaultH)),
                widthNorm: defaultW,
                heightNorm: defaultH,
            }];
        });
        setActiveTool(null);
    };

    const removePlacement = (fieldType, page) => {
        setPlacements(prev => prev.filter(p => !(p.fieldType === fieldType && p.page === page)));
    };

    const savePlacements = async () => {
        if (placementSavingRef.current) return;
        placementSavingRef.current = true;
        try {
            const json = placements.length > 0 ? JSON.stringify(placements) : null;
            await agreementsAPI.updateMeta(placementForm.id, {
                title: placementForm.title,
                isActive: placementForm.isActive,
                requireSignature: placementForm.requireSignature,
                sortOrder: placementForm.sortOrder,
                fieldPlacementsJson: json,
            });
            showToast('Placements saved', 'success');
            closePlacementEditor();
            fetchForms();
        } catch (err) {
            console.error('Save placements error:', err);
            showToast('Failed to save placements', 'error');
        } finally {
            placementSavingRef.current = false;
        }
    };

    const FIELD_COLORS = { Signature: '#1976d2', Date: '#388e3c', PrintedName: '#f57c00' };

    // ── Render ──────────────────────────────────────────────────────────────

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Agreement Forms</Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => setUploadOpen(true)}>
                    Upload Form
                </Button>
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
            ) : forms.length === 0 ? (
                <Alert severity="info">No agreement forms yet. Upload a PDF to get started.</Alert>
            ) : (
                <List disablePadding>
                    {forms.map((form, idx) => (
                        <Paper key={form.id} variant="outlined" sx={{ mb: 1 }}>
                            <ListItem>
                                <ListItemText
                                    primary={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                            <Typography fontWeight={600}>{form.title}</Typography>
                                            <Chip size="small" label={form.isActive ? 'Active' : 'Inactive'}
                                                color={form.isActive ? 'success' : 'default'} />
                                            {form.requireSignature && <Chip size="small" label="Required" color="error" />}
                                            {form.fieldPlacementsJson
                                                ? <Chip size="small" label="Placements set" color="info" />
                                                : <Chip size="small" label="No placements (fallback)" />}
                                        </Box>
                                    }
                                    secondary={`Sort order: ${form.sortOrder} · Updated ${new Date(form.updatedUtc).toLocaleDateString()}`}
                                />
                                <ListItemSecondaryAction>
                                    <Stack direction="row" spacing={0.5}>
                                        <Tooltip title="Move up">
                                            <span><IconButton size="small" onClick={() => handleReorder(form, 'up')} disabled={idx === 0}><UpIcon fontSize="small" /></IconButton></span>
                                        </Tooltip>
                                        <Tooltip title="Move down">
                                            <span><IconButton size="small" onClick={() => handleReorder(form, 'down')} disabled={idx === forms.length - 1}><DownIcon fontSize="small" /></IconButton></span>
                                        </Tooltip>
                                        <Tooltip title="View PDF"><IconButton size="small" onClick={() => handleView(form)}><ViewIcon fontSize="small" /></IconButton></Tooltip>
                                        <Tooltip title="Download PDF"><IconButton size="small" onClick={() => handleDownload(form)}><DownloadIcon fontSize="small" /></IconButton></Tooltip>
                                        <Tooltip title="Set signature placement"><IconButton size="small" color="primary" onClick={() => openPlacementEditor(form)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                                        <Tooltip title="Edit details"><IconButton size="small" onClick={() => openEdit(form)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                                        <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => handleDelete(form)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                                    </Stack>
                                </ListItemSecondaryAction>
                            </ListItem>
                        </Paper>
                    ))}
                </List>
            )}

            {/* ── Upload dialog ── */}
            <Dialog open={uploadOpen} onClose={() => setUploadOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Upload Agreement Form</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField label="Form Title" value={uploadTitle} onChange={e => setUploadTitle(e.target.value)} fullWidth required />
                        <Box>
                            <Button variant="outlined" onClick={() => uploadInputRef.current?.click()}>
                                {uploadFile ? uploadFile.name : 'Select PDF file'}
                            </Button>
                            <input ref={uploadInputRef} type="file" accept="application/pdf" style={{ display: 'none' }}
                                onChange={e => setUploadFile(e.target.files?.[0] ?? null)} />
                        </Box>
                        <FormControlLabel
                            control={<Switch checked={uploadRequire} onChange={e => setUploadRequire(e.target.checked)} />}
                            label="Require signature to submit application"
                        />
                        <Alert severity="info">
                            After uploading, use the placement editor (pencil icon) to mark where the signature, date, and name should appear on the PDF.
                            Without placements, a signature certification page is appended automatically.
                        </Alert>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setUploadOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleUploadSubmit} disabled={uploading}>
                        {uploading ? <CircularProgress size={18} /> : 'Upload'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Edit meta dialog ── */}
            <Dialog open={!!editForm} onClose={() => setEditForm(null)} maxWidth="sm" fullWidth>
                <DialogTitle>Edit Agreement Form</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField label="Title" value={editTitle} onChange={e => setEditTitle(e.target.value)} fullWidth />
                        <TextField label="Sort Order" type="number" value={editSortOrder} onChange={e => setEditSortOrder(Number(e.target.value))} fullWidth />
                        <FormControlLabel
                            control={<Switch checked={editActive} onChange={e => setEditActive(e.target.checked)} />}
                            label="Active (shown to applicants)"
                        />
                        <FormControlLabel
                            control={<Switch checked={editRequire} onChange={e => setEditRequire(e.target.checked)} />}
                            label="Require signature"
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditForm(null)}>Cancel</Button>
                    <Button variant="contained" onClick={handleEditSave} disabled={saving}>
                        {saving ? <CircularProgress size={18} /> : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── PDF viewer dialog ── */}
            <Dialog open={viewerOpen} onClose={closeViewer} maxWidth="lg" fullWidth PaperProps={{ sx: { height: '90vh' } }}>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    Agreement Preview
                    <IconButton onClick={closeViewer}><CloseIcon /></IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 0, position: 'relative' }}>
                    {viewerLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                            <CircularProgress />
                        </Box>
                    ) : viewerUrl ? (
                        <iframe src={viewerUrl} style={{ width: '100%', height: '100%', border: 'none' }} title="Agreement PDF" />
                    ) : null}
                </DialogContent>
            </Dialog>

            {/* ── Placement editor dialog ── */}
            <Dialog open={!!placementForm} onClose={closePlacementEditor} maxWidth="lg" fullWidth
                PaperProps={{ sx: { height: '92vh', display: 'flex', flexDirection: 'column' } }}>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
                    <Box>
                        <Typography variant="h6">Set Signature Placements</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Click a field type, then click on the PDF where it should appear.
                        </Typography>
                    </Box>
                    <IconButton onClick={closePlacementEditor}><CloseIcon /></IconButton>
                </DialogTitle>
                <Divider />
                <Box sx={{ display: 'flex', gap: 1, px: 2, py: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                    {['Signature', 'Date', 'PrintedName'].map(ft => (
                        <Button key={ft} variant={activeTool === ft ? 'contained' : 'outlined'}
                            size="small"
                            sx={{
                                borderColor: FIELD_COLORS[ft], color: activeTool === ft ? '#fff' : FIELD_COLORS[ft],
                                bgcolor: activeTool === ft ? FIELD_COLORS[ft] : undefined
                            }}
                            onClick={() => setActiveTool(activeTool === ft ? null : ft)}>
                            + {ft}
                        </Button>
                    ))}
                    <Divider orientation="vertical" flexItem />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <IconButton size="small" onClick={() => setCurrentPage(p => Math.max(0, p - 1))} disabled={currentPage === 0}>
                            <PrevPageIcon fontSize="small" />
                        </IconButton>
                        <Typography variant="caption" sx={{ minWidth: 70, textAlign: 'center' }}>
                            Page {currentPage + 1} / {pdfPages}
                        </Typography>
                        <IconButton size="small" onClick={() => setCurrentPage(p => Math.min(pdfPages - 1, p + 1))} disabled={currentPage >= pdfPages - 1}>
                            <NextPageIcon fontSize="small" />
                        </IconButton>
                    </Box>
                    <Divider orientation="vertical" flexItem />
                    <Typography variant="caption" color="text.secondary">
                        Current placements:
                    </Typography>
                    {placements.map(p => (
                        <Chip key={`${p.fieldType}-${p.page}`} size="small"
                            label={`${p.fieldType} (p.${p.page + 1})`}
                            onDelete={() => removePlacement(p.fieldType, p.page)}
                            sx={{ bgcolor: FIELD_COLORS[p.fieldType], color: '#fff', '& .MuiChip-deleteIcon': { color: 'rgba(255,255,255,0.7)' } }}
                        />
                    ))}
                    {activeTool && (
                        <Alert severity="info" sx={{ py: 0, px: 1, flex: 1 }}>
                            Click on the PDF below to place the <strong>{activeTool}</strong> field
                        </Alert>
                    )}
                </Box>
                <DialogContent
                    sx={{ p: 0, flex: 1, overflow: 'auto', cursor: activeTool ? 'crosshair' : 'default', display: 'flex', justifyContent: 'center', bgcolor: '#525659' }}
                    onClick={handlePlacementClick}>
                    {pdfDoc ? (
                        <Box sx={{ position: 'relative', alignSelf: 'flex-start', lineHeight: 0, m: 2 }}>
                            <canvas ref={canvasRef} style={{ display: 'block', boxShadow: '0 2px 8px rgba(0,0,0,0.5)' }} />
                            {/* Overlay markers — positioned as % of canvas */}
                            <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
                                {placements.filter(p => p.page === currentPage).map(p => (
                                    <Box key={p.fieldType} sx={{
                                        position: 'absolute',
                                        left: `${p.xNorm * 100}%`,
                                        top: `${p.yNorm * 100}%`,
                                        width: `${p.widthNorm * 100}%`,
                                        height: `${p.heightNorm * 100}%`,
                                        border: `2px dashed ${FIELD_COLORS[p.fieldType]}`,
                                        bgcolor: `${FIELD_COLORS[p.fieldType]}22`,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    }}>
                                        <Typography variant="caption" sx={{ color: FIELD_COLORS[p.fieldType], fontWeight: 700, bgcolor: 'white', px: 0.5, borderRadius: 0.5 }}>
                                            {p.fieldType}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                            <CircularProgress sx={{ color: 'white' }} />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                        Without placements, a signature certification page is appended instead.
                    </Typography>
                    <Button onClick={closePlacementEditor}>Cancel</Button>
                    <Button variant="contained" onClick={savePlacements}>Save Placements</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
